import { connect, ResolveThunks } from "react-redux";
import { RootState } from "state/rootReducer";
import { actions } from "state/slices/songsSlice";
import SongListItem from "./SongListItem";

interface OwnProps {
  songs: Song[];
  otherdata?: (string | JSX.Element)[];
  otherdataName?: string;
  /**
   * "controls" gives the extra column room for interactive controls and keeps
   * it on small screens (Time gives way instead). Defaults to "text".
   */
  otherdataVariant?: "text" | "controls";
  selectSongHandler: (song: Song) => void;
  deleteSongHandler?: (song: Song) => void;
  overridePlayHandler?: (song: Song, selected: boolean) => void;
  selectedSongsByGid?: Record<string, boolean>;
  maxSongs?: number;
}

interface StateProps {
  currentlyPlayingTrack: string | null;
}

interface DispatchProps {
  onAddToPlaylist: typeof actions.addToPlaylist;
}

export type SongListProps = OwnProps &
  StateProps &
  ResolveThunks<DispatchProps>;

export function SongList(props: SongListProps): JSX.Element {
  const max = props.maxSongs || 100;
  function render(): JSX.Element {
    return (
      // No scroll box of its own: the page (or a modal's body) scrolls. With
      // one, a long list was squeezed into whatever height the controls above
      // it left, which on phones was three rows, and the controls never
      // scrolled out of the way.
      <div className="flex flex-col">
        {renderHeader(props.deleteSongHandler !== undefined)}
        <div className="bg-surface">
          {props.songs
            .slice(0, max)
            .map((song, index) =>
              renderSong(
                song,
                song.gid === props.currentlyPlayingTrack,
                index,
                props.otherdata ? props.otherdata[index] : undefined,
                props.selectedSongsByGid
                  ? props.selectedSongsByGid[song.gid]
                  : true
              )
            )}
          {props.songs.length > max && (
            <span className="text-xs sm:text-sm md:text-base text-left font-bold block text-ink font-sans">
              {props.songs.length - max} More Songs...
            </span>
          )}
        </div>
      </div>
    );
  }

  function renderHeader(_includeSecondButton: boolean): JSX.Element {
    // Shares .song-grid with the rows, so the columns cannot drift apart.
    const gridClass = !props.otherdata
      ? "song-grid"
      : props.otherdataVariant === "controls"
        ? "song-grid song-grid--controls"
        : "song-grid song-grid--extra";

    return (
      <div
        className={`${gridClass} px-2 py-1.5 border-b border-rule-strong text-xs uppercase tracking-wide text-muted`}
      >
        <div>Track</div>
        {props.otherdata && (
          <div className="song-extra">{props.otherdataName}</div>
        )}
        <div className="song-time text-right">Time</div>
        <div aria-hidden="true"></div>
      </div>
    );
  }

  function renderSong(
    song: Song,
    playing: boolean,
    index: number,
    otherdata?: string | JSX.Element,
    selected?: boolean
  ): JSX.Element {
    return (
      <SongListItem
        song={song}
        otherdata={otherdata}
        otherdataVariant={props.otherdataVariant}
        index={index}
        selectSongHandler={props.selectSongHandler}
        deleteSongHandler={props.deleteSongHandler}
        overridePlayHandler={props.overridePlayHandler}
        addStartingAtSong={(song) => {
          const start = props.songs.findIndex((s) => s.gid === song.gid);
          props.onAddToPlaylist(props.songs.slice(start));
        }}
        key={song.gid}
        currentlyPlaying={playing}
        currentlySelected={selected}
      />
    );
  }

  return render();
}

function mapState(state: RootState): StateProps {
  return {
    currentlyPlayingTrack: state.songs.currentTrackId,
  };
}

const mapDispatch: DispatchProps = {
  onAddToPlaylist: actions.addToPlaylist,
};

export default connect(mapState, mapDispatch)(SongList);
